import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Send } from "lucide-react";

const BUILDINGS = {
  "Plaza Barat": { base: 412, tenants: [["A-01 Kopi Senja", 64], ["A-07 Laundry", 9], ["B-12 Apotek", 41], ["B-15 Warung Nasi", 6], ["C-03 Barbershop", 77]] },
  "Food Court": { base: 268, tenants: [["FC-02 Bakso", 33], ["FC-05 Es Teh", 4], ["FC-09 Ayam Geprek", 52], ["FC-11 Mie Aceh", 18], ["FC-14 Dimsum", 71]] },
  Residence: { base: 186, tenants: [["12B Rina", 8], ["12C Budi", 58], ["14A Sari", 39], ["15D Anton", 3], ["16F Dewi", 66]] },
};

const seed = (base) => Array.from({ length: 28 }, (_, i) => base * (0.7 + 0.3 * Math.abs(Math.sin(i * 0.7))));

const Stat = ({ label, value, accent, testId }) => (
  <div className="rounded-xl border border-white/[0.06] bg-white/[0.03] p-3">
    <p className="font-mono-d text-[10.5px] uppercase tracking-[0.14em] text-zinc-400">{label}</p>
    <p data-testid={testId} className={`mt-1 font-mono-d text-lg tabular-nums ${accent ? "text-[var(--dash)]" : "text-zinc-50"}`}>{value}</p>
  </div>
);

export const BMDashboard = () => {
  const [bld, setBld] = useState("Plaza Barat");
  const { base, tenants } = BUILDINGS[bld];
  const [series, setSeries] = useState(() => seed(412));
  const [kwh, setKwh] = useState(3184.2);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    setSeries(seed(base));
    setSent(false);
  }, [base]);

  useEffect(() => {
    const id = setInterval(() => {
      setSeries((s) => [...s.slice(1), base * (0.75 + Math.random() * 0.3)]);
      setKwh((k) => k + Math.random() * 0.12);
    }, 1000);
    return () => clearInterval(id);
  }, [base]);

  const live = series[series.length - 1];
  const max = Math.max(...series) * 1.1;
  const low = tenants.filter(([, v]) => v < 10);

  const remind = () => {
    setSent(true);
    toast.success(`Dash AI sent reminders to ${low.length} low-credit tenants`, { description: `${bld} · via in-app chat & push` });
  };

  return (
    <div data-testid="mockup-bm-dashboard" className="flex h-full min-h-[520px] flex-col overflow-hidden rounded-3xl border border-white/10 bg-[#0b0d12] shadow-[0_30px_80px_-40px_rgba(255,107,0,0.5)] lg:max-h-[580px] lg:min-h-0">
      <div className="flex items-center justify-between gap-3 border-b border-white/5 px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="relative flex h-2 w-2"><span className="pulse-ring absolute inset-0 rounded-full bg-emerald-400" /><span className="h-2 w-2 rounded-full bg-emerald-400" /></span>
          <p className="font-mono-d text-[11px] uppercase tracking-[0.18em] text-zinc-300">Live · Demo data</p>
        </div>
        <div className="flex gap-1 rounded-full bg-white/[0.04] p-1">
          {Object.keys(BUILDINGS).map((b) => (
            <button key={b} onClick={() => setBld(b)} data-testid={`bm-building-${b.replace(/\s/g, "-").toLowerCase()}`} className={`rounded-full px-2.5 py-1 text-[11px] transition-colors ${b === bld ? "bg-[var(--dash)] text-black" : "text-zinc-300 hover:text-white"}`}>
              {b}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 p-3">
        <Stat label="Live load" value={`${live.toFixed(1)} kW`} accent testId="bm-live-load" />
        <Stat label="Today" value={`${kwh.toFixed(1)} kWh`} testId="bm-today-kwh" />
        <Stat label="Low credit" value={`${low.length} units`} testId="bm-low-credit" />
      </div>
      <div className="relative mx-3 h-28 rounded-xl border border-white/[0.06] bg-white/[0.02] lg:h-auto lg:flex-1" data-testid="bm-live-chart">
        <div className="absolute inset-3 flex items-end gap-[3px]">
        {series.map((v, i) => (
          <motion.div key={i} className={`flex-1 rounded-sm ${i === series.length - 1 ? "bg-[var(--dash)]" : "bg-dash/35"}`} animate={{ height: `${(v / max) * 100}%` }} transition={{ duration: 0.5 }} />
        ))}
        </div>
      </div>
      <div className="mt-3 space-y-1 px-3" data-testid="bm-tenant-table">
        {tenants.map(([name, v]) => (
          <div key={name} className="flex items-center gap-3 rounded-lg px-2 py-1.5 hover:bg-white/[0.03]">
            <p className="w-32 truncate text-xs text-zinc-200">{name}</p>
            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-white/[0.06]">
              <div className={`h-full rounded-full ${v < 10 ? "bg-red-400" : "bg-emerald-400"}`} style={{ width: `${v}%` }} />
            </div>
            <span className={`w-16 text-right font-mono-d text-[11px] ${v < 10 ? "text-red-300" : "text-zinc-400"}`}>{v < 10 ? (sent ? "Reminded" : "Low") : `${v} kWh`}</span>
          </div>
        ))}
      </div>
      <div className="p-3">
        <button onClick={remind} data-testid="bm-send-reminders-button" className="flex w-full items-center justify-center gap-2 rounded-xl bg-[var(--dash)] py-2.5 text-sm font-semibold text-black transition-[background-color,transform] hover:bg-[#ff8a33] active:scale-[0.98]">
          <Send size={15} /> Let Dash AI remind low-credit tenants
        </button>
      </div>
    </div>
  );
};
